import { useEffect, useRef, useState } from 'react';
import { ArrowRight, Zap } from 'lucide-react';
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = '0px'
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  useEffect(() => {
    const node = ref.current;
    if (!node) {
      return;
    }
    if (typeof window === 'undefined' || typeof IntersectionObserver === 'undefined') {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) {
          observer.unobserve(entry.target);
        }
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => {
      observer.disconnect();
    };
  }, [once, rootMargin, threshold]);
  return {
    ref,
    isVisible
  };
}
export default function SecureYourSlot() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.3
  });
  return <section data-section-id="1536" id="initiate-your-transformation" className="dark bg-[#1A2A4E] text-white relative w-full overflow-hidden py-32 md:py-48 border-t-4 border-[#D4AF37]" ref={ref}>
            {}
            <div className="absolute inset-0 bg-[#1A2A4E] z-0">
                {}
                <div className="absolute inset-0 opacity-5 mix-blend-overlay" style={{
        backgroundImage: `
                            linear-gradient(45deg, transparent 48%, #D4AF37 48%, #D4AF37 52%, transparent 52%),
                            linear-gradient(-45deg, transparent 48%, #D4AF37 48%, #D4AF37 52%, transparent 52%)
                        `,
        backgroundSize: '120px 120px'
      }} />
                <div className="absolute inset-0 opacity-20" style={{
        backgroundImage: 'radial-gradient(circle at 50% 50%, transparent 0%, #000 100%)'
      }} />
            </div>

            {}
            <div className={`relative z-10 container mx-auto px-4 flex flex-col items-center justify-center text-center transition-all duration-1000 transform ${isVisible ? 'translate-y-0 opacity-100' : 'translate-y-10 opacity-0'}`}>
                
                {}
                <h2 className="text-white text-6xl md:text-8xl lg:text-9xl font-bold tracking-tighter mb-12 uppercase italic transform -skew-x-6 drop-shadow-2xl">
                    Ready to Lead the <span className="inline-block border-b-8 border-[#D4AF37] pb-2">AI Revolution?</span>
                </h2>

                {}
                <Link to="/LetsTalkPage" className="group">
                    <Button size="lg" className="h-20 md:h-24 px-12 md:px-16 text-xl md:text-3xl font-bold bg-[#D4AF37] text-[#1A2A4E] hover:bg-white hover:text-[#1A2A4E] hover:scale-105 transition-all duration-300 shadow-[0_0_50px_-10px_rgba(212,175,55,0.4)] border-2 border-transparent hover:border-[#D4AF37] uppercase tracking-widest rounded-sm skew-x-[-6deg]">
                        <span className="skew-x-[6deg] flex items-center gap-4">
                            Book Discovery Call
                            <Zap className="w-8 h-8 md:w-10 md:h-10 fill-current text-[#1A2A4E]" />
                        </span>
                    </Button>
                </Link>

                {}
                <div className="absolute top-1/2 left-0 w-full h-1 bg-[#D4AF37]/5 -rotate-2 pointer-events-none" />
                <div className="absolute top-1/2 left-0 w-full h-1 bg-[#D4AF37]/5 rotate-2 pointer-events-none" />
            </div>
        </section>;
}